import { useState, useEffect, useCallback, useRef } from 'react'

const VOLUME_INDICATOR_DURATION = 3000
const VOLUME_STEP = 10
const DEFAULT_VOLUME = 50
const STORAGE_KEY = 'system_volume'

const getSavedVolume = (): number => {
  const saved = localStorage.getItem(STORAGE_KEY)
  return saved ? parseInt(saved, 10) : DEFAULT_VOLUME
}

const clampVolume = (value: number): number => Math.max(0, Math.min(100, value))

export function useVolumeControl() {
  const [volume, setVolume] = useState(getSavedVolume)
  const [showIndicator, setShowIndicator] = useState(false)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const volumeRef = useRef(volume)

  useEffect(() => {
    volumeRef.current = volume
  }, [volume])

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, volume.toString())
  }, [volume])

  const showVolumeIndicator = useCallback(() => {
    setShowIndicator(true)
    
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
    }

    hideTimeoutRef.current = setTimeout(() => {
      setShowIndicator(false)
      hideTimeoutRef.current = null
    }, VOLUME_INDICATOR_DURATION)
  }, [])

  const adjustVolume = useCallback((delta: number) => {
    setVolume(prev => clampVolume(prev + delta))
    showVolumeIndicator()
  }, [showVolumeIndicator])

  const setVolumeValue = useCallback((value: number) => {
    setVolume(clampVolume(value))
    showVolumeIndicator()
  }, [showVolumeIndicator])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return

      const isMacCmd = e.metaKey
      const isWinCtrl = e.ctrlKey
      const isModifier = isMacCmd || isWinCtrl

      if (e.key === 'F11' || (isModifier && e.key === 'ArrowUp')) {
        e.preventDefault()
        adjustVolume(VOLUME_STEP)
      } else if (e.key === 'F10' || (isModifier && e.key === 'ArrowDown')) {
        e.preventDefault()
        adjustVolume(-VOLUME_STEP)
      } else if (e.key === 'F9' || (isModifier && e.key === 'm')) {
        e.preventDefault()
        setVolumeValue(volumeRef.current > 0 ? 0 : DEFAULT_VOLUME)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [adjustVolume, setVolumeValue])

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [])

  return {
    volume,
    showIndicator,
    adjustVolume,
    setVolume: setVolumeValue,
    showVolumeIndicator
  }
}